import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { INITIAL_STEP, UploadCsvStatusEnum, UploadStepsEnum } from '../../../constants/addressBook';
import { IColumnMapping, IRow, TInvalidRows, TParsedData } from '../../../types/addressBook';
import { ICsvUploadProfile, TUseCsvUploadController } from './csvUploadProfile';
import { useColumnMapping, useCsvUpload, useRows, useStepNavigation } from './hooks';

interface IContext {
  currentStep: number;
  visitedStep: number;
  fileCSV: File | null;
  dataAddresses: TParsedData[] | null;
  status: UploadCsvStatusEnum;
  mappedColumns: IColumnMapping[];
  amountOfIssues: number;
  showIssuesOnly: boolean;
  rows: IRow[];
  invalidRows: TInvalidRows;
  userHeaders: string[];
  hasRows: boolean;
  isLoading: boolean;
  isImportBlocked: boolean;
  successLabel: string;
  profile: ICsvUploadProfile;
  deletedRowsStackRef: React.RefObject<IRow[]>;
  isCellEditable?: (row: IRow, field: string) => boolean;

  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  setRows: React.Dispatch<React.SetStateAction<IRow[]>>;
  setShowIssuesOnly: React.Dispatch<React.SetStateAction<boolean>>;
  resetCompletionFrom: (stepNum: number) => void;
  handleMapColumns: (id: string, value: string) => void;
  handleDeleteFile: () => void;
  clearModalState: () => void;
  handleUploadFile: (file: File) => Promise<void>;
  handleNextStep: () => void;
  handleBackStep: () => void;
  handleShowIssuesOnly: () => void;
  handleShowAllRows: () => void;
  handleStepClickButton: (stepNum: number) => void;
  handleCloseWithSuccess: (onClose: () => void) => void;
  handleFileSizeError: () => void;
}

const UploadModalContext = createContext<IContext | undefined>(undefined);

interface IProps {
  children: React.ReactNode;
  isOpen: boolean;
  profile: ICsvUploadProfile;
  useController: TUseCsvUploadController;
}

export const UploadModalProvider: React.FC<IProps> = ({ children, isOpen, profile, useController }) => {
  const [currentStep, setCurrentStep] = useState(INITIAL_STEP);
  const [visitedStep, setVisitedStep] = useState(INITIAL_STEP);
  const [showIssuesOnly, setShowIssuesOnly] = useState(false);

  const controller = useController();

  const { fileCSV, dataAddresses, status, setStatus, handleUploadFile, handleDeleteFile, handleFileSizeError } =
    useCsvUpload({ requiredColumnCount: profile.requiredColumnCount });

  const { mappedColumns, userHeaders, amountOfIssues, handleMapColumns, clearMappedColumns } = useColumnMapping({
    dataAddresses,
    columns: profile.columns,
  });

  const stableDataAddresses = useMemo(() => dataAddresses ?? [], [dataAddresses]);

  const { rows, setRows, invalidRows, hasRows, deletedRowsStackRef, rowsRef } = useRows({
    dataAddresses: stableDataAddresses,
    mappedColumns,
    validateRows: controller.validateRows,
  });

  const { handleNextStep, handleBackStep, handleStepClickButton } = useStepNavigation({
    setCurrentStep,
    visitedStep,
  });

  const isImportStep = useMemo(() => currentStep === UploadStepsEnum.Import, [currentStep]);

  const clearModalState = useCallback(() => {
    setCurrentStep(INITIAL_STEP);
    setVisitedStep(INITIAL_STEP);
    setStatus(UploadCsvStatusEnum.Idle);
    setShowIssuesOnly(false);
    setRows([]);
    handleDeleteFile();
    clearMappedColumns();
    controller.resetCount();
  }, [handleDeleteFile, clearMappedColumns, setStatus, controller.resetCount]);

  const resetCompletionFrom = useCallback((stepNum: number) => {
    setVisitedStep((prev) => Math.min(prev, stepNum - 1));
  }, []);

  // reset the deleted rows stack when the file or columns change
  useEffect(() => {
    deletedRowsStackRef.current = [];
  }, [fileCSV, mappedColumns]);

  // if something changed on the step, reset completedStep to the current step
  useEffect(() => {
    resetCompletionFrom(currentStep);
  }, [mappedColumns, fileCSV, rows]);

  useEffect(() => {
    if (!isOpen) {
      clearModalState();
    }
  }, [isOpen, clearModalState]);

  useEffect(() => {
    setVisitedStep((prev) => Math.max(prev, Math.min(currentStep, UploadStepsEnum.Repair)));
  }, [currentStep]);

  // it's true when we dispatch addresses to the server, while it's true, we cannot dispatch another request.
  const hasDispatchedImportRef = useRef(false);

  useEffect(() => {
    if (isImportStep && !hasDispatchedImportRef.current) {
      controller.submit(rowsRef.current);
      hasDispatchedImportRef.current = true;
    }

    // reset the flag when we navigate away from Import step
    if (!isImportStep) {
      hasDispatchedImportRef.current = false;
    }
  }, [isImportStep, controller.submit]);

  useEffect(() => {
    if (!isImportStep) {
      return;
    }

    if (controller.hasImportError) {
      controller.onImportError();
      setCurrentStep(UploadStepsEnum.Repair);
      return;
    }

    if (controller.importInvalidRows.length > 0) {
      controller.onImportInvalid(controller.importInvalidRows.length);
      setRows(controller.importInvalidRows);
      setCurrentStep(UploadStepsEnum.Repair);
      return;
    }
  }, [
    controller.importInvalidRows,
    controller.hasImportError,
    controller.onImportError,
    controller.onImportInvalid,
    isImportStep,
  ]);

  const handleShowIssuesOnly = useCallback(() => {
    setShowIssuesOnly(true);
  }, []);

  const handleShowAllRows = useCallback(() => {
    setShowIssuesOnly(false);
  }, []);

  const value = useMemo(
    () => ({
      currentStep,
      visitedStep,
      fileCSV,
      dataAddresses,
      status,
      mappedColumns,
      amountOfIssues,
      showIssuesOnly,
      rows,
      invalidRows,
      userHeaders,
      hasRows,
      isLoading: controller.isLoading,
      isImportBlocked: controller.isImportBlocked,
      successLabel: controller.successLabel,
      profile,
      deletedRowsStackRef,
      isCellEditable: controller.isCellEditable,
      setCurrentStep,
      setRows,
      setShowIssuesOnly,
      resetCompletionFrom,
      handleMapColumns,
      handleDeleteFile,
      clearModalState,
      handleUploadFile,
      handleNextStep,
      handleBackStep,
      handleShowIssuesOnly,
      handleShowAllRows,
      handleStepClickButton,
      handleCloseWithSuccess: controller.onCloseWithSuccess,
      handleFileSizeError,
    }),
    [
      currentStep,
      visitedStep,
      fileCSV,
      dataAddresses,
      status,
      mappedColumns,
      amountOfIssues,
      showIssuesOnly,
      rows,
      deletedRowsStackRef,
      setRows,
      invalidRows,
      userHeaders,
      hasRows,
      controller.isLoading,
      controller.isImportBlocked,
      controller.successLabel,
      controller.onCloseWithSuccess,
      controller.isCellEditable,
      profile,
      resetCompletionFrom,
      handleMapColumns,
      handleDeleteFile,
      clearModalState,
      handleUploadFile,
      handleNextStep,
      handleBackStep,
      handleShowIssuesOnly,
      handleShowAllRows,
      handleStepClickButton,
      handleFileSizeError,
    ],
  );

  return <UploadModalContext.Provider value={value}>{children}</UploadModalContext.Provider>;
};

export const useUploadModal = (): IContext => {
  const context = useContext(UploadModalContext);
  return context as IContext;
};
